import { type ReactNode, useEffect, useState } from 'react';
import { PageViewTracker } from '@belong/common';
import { Accordion, Footer, Text, Image } from '@belong/ui';
import { clsx } from 'clsx';
import { Header } from 'src/components/header/header';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { StringComponent } from 'src/features/faqs/components/string/String';
import css from 'src/features/faqs/faq.module.css';
import { FAQS_STRINGS } from 'src/features/faqs/faqs.strings';
import { SeoHint } from 'src/features/home-page/components/seo-hint/seo-hint';

export default function FAQ() {
  // Indexes are initialized in -1 for compatibility with accordion
  const [selectedQuestionHomeOwner, setSelectedQuestionHomeOwner] = useState(-1);
  const [selectedQuestionResident, setSelectedQuestionResident] = useState(-1);

  const handleClickAccordion =
    (currVal: number, updateFunc: React.Dispatch<React.SetStateAction<number>>) => (selectedOption: number) => {
      const nextSelectedOption = selectedOption === currVal ? -1 : selectedOption;
      updateFunc(nextSelectedOption);
    };

  const itemToAccordion = (letterId: 'h' | 'r') =>
    function it(item: { q: string; a: string | string[] }, index: number) {
      return {
        title: <div id={`${letterId}-${index}`}>{item.q}</div>,
        content: <StringComponent string={item.a} />,
      };
    };

  const homeownerAccordionItems = FAQS_STRINGS.homeowner.map(itemToAccordion('h'));
  const residentAccordionItems = FAQS_STRINGS.resident.map(itemToAccordion('r'));

  useEffect(() => {
    const hashId = window?.location?.hash || '';
    const [, groupLetter, selectedQuestion]: [string, string, string] =
      (/#([r|h])\-([0-9]*)/g.exec(hashId) as unknown as [string, string, string]) || [];

    if (groupLetter && selectedQuestion) {
      const setSelectedQuestion = groupLetter === 'h' ? setSelectedQuestionHomeOwner : setSelectedQuestionResident;

      setSelectedQuestion(Number(selectedQuestion));
    }
  }, []);

  return (
    <PageViewTracker name="faq">
      <MetaSEO
        title={FAQS_STRINGS['meta.title']}
        description={FAQS_STRINGS['meta.description']}
        openGraph={{ title: FAQS_STRINGS['meta.title'], description: FAQS_STRINGS['meta.description'] }}
      />
      <div className="-mt-2xl pt-2xl relative overflow-hidden">
        <div className={clsx(css.gradient)} />
        <SeoHint />
        <div className="isolate">
          <div className="w-full relative lg:container xl:max-w-container-xl">
            <Image src="/home-page/cloud.png" alt="" className={clsx(css.leftCloud)} />
            <Image src="/home-page/cloud.png" alt="" className={clsx(css.leftSmallCloud)} />
            <Image src="/home-page/balloon.svg" alt="" className={clsx(css.balloon)} />
            <Image src="/home-page/cloud.png" alt="" className={clsx(css.rightCloud)} />
            <Image src="/home-page/sun.png" alt="" className={clsx(css.sun)} />
            <div className="pt-3xl md:pt-4xl mb-5xl flex flex-col gap-sm">
              <Text as="h1" variant="h1" fontWeight="semibold" className="text-center">
                {FAQS_STRINGS['page.title.1']} <br />
                {FAQS_STRINGS['page.title.2']}
              </Text>
              <div className={clsx('mx-auto', css.description)}>
                <Text as="h2" className="text-center">
                  {FAQS_STRINGS['page.subtitle']}
                </Text>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="w-full mb-sm">
            <Text variant="h2" fontWeight="semibold">
              {FAQS_STRINGS.homeowner_heading}
            </Text>
          </div>
          <Accordion
            index={selectedQuestionHomeOwner}
            onChange={handleClickAccordion(selectedQuestionHomeOwner, setSelectedQuestionHomeOwner)}
            items={homeownerAccordionItems}
          />
          <div className="mt-xl mb-sm">
            <Text variant="h2" fontWeight="semibold">
              {FAQS_STRINGS.resident_heading}
            </Text>
          </div>
          <Accordion
            index={selectedQuestionResident}
            onChange={handleClickAccordion(selectedQuestionResident, setSelectedQuestionResident)}
            items={residentAccordionItems}
          />
          <div className="mb-sm" />
        </div>
      </div>
    </PageViewTracker>
  );
}

FAQ.getLayout = (page: ReactNode): ReactNode => {
  return (
    <div className="relative flex flex-col min-h-full overflow-x-hidden">
      <div className="relative z-10">
        <Header variant="tertiary" />
      </div>
      <main className="flex-1">{page}</main>
      <Footer />
    </div>
  );
};
